import { ClipboardList } from 'lucide-react'
import { PublicHeader } from '../../../components/Navigation/PublicHeader'
import { AppLayout, AppContent } from '../../../styles/AppStyles'
import { SurveyQuestionInput, type SurveyAnswer } from '../components/questions'
import { groupQuestionsBySource } from '../../validation'
import type { SurveyQuestion } from '@bandos/shared'
import { defaultValue, isAnswered } from './surveyParticipantUtils'
import {
  AnsweredDot,
  BrandRow,
  Content,
  Header,
  Meta,
  Page,
  PoweredBy,
  ProgressText,
  QuestionCard,
  QuestionIndex,
  QuestionMeta,
  SubmitButton,
  SubmitSection,
  SubmitSectionInner,
} from './SurveyParticipantPageStyles'

export interface SurveyParticipantFormProps {
  questions: SurveyQuestion[]
  answers: Record<number, SurveyAnswer>
  answeredCount: number
  submitting: boolean
  submitError: boolean
  onChange: (index: number, value: SurveyAnswer) => void
  onSubmit: () => void
}

/**
 * The active question form rendered when the survey is open and unsubmitted.
 * The page hosts state and effects; this component is a pure render of the
 * sectioned question grid + submit footer.
 */
export function SurveyParticipantForm({
  questions,
  answers,
  answeredCount,
  submitting,
  submitError,
  onChange,
  onSubmit,
}: SurveyParticipantFormProps) {
  const total = questions.length

  return (
    <AppLayout>
      <PublicHeader showReadOnlyIndicator={false} />
      <AppContent>
        <Page>
          <Content>
            <Header>
              <BrandRow>
                <ClipboardList size={14} />
                Validation Survey
              </BrandRow>
              <Meta>{total} questions</Meta>
            </Header>

            {groupQuestionsBySource(questions).map((section) => (
              <div key={section.sourceNodeId ?? 'all'}>
                {section.items.map(({ question: q, index: i }) => (
                  <QuestionCard key={i} $answered={isAnswered(answers[i])}>
                    <QuestionMeta>
                      <QuestionIndex>
                        Question {i + 1} of {total}
                      </QuestionIndex>
                      {isAnswered(answers[i]) && <AnsweredDot />}
                    </QuestionMeta>
                    <SurveyQuestionInput
                      question={q}
                      value={answers[i] ?? defaultValue(q.type)}
                      onChange={(v) => onChange(i, v)}
                    />
                  </QuestionCard>
                ))}
              </div>
            ))}

            <SubmitSection>
              <SubmitSectionInner>
                <ProgressText>
                  {answeredCount} of {total} answered
                </ProgressText>
                {submitError && (
                  <ProgressText style={{ color: '#ef4444' }}>
                    Something went wrong. Please try again.
                  </ProgressText>
                )}
                <SubmitButton $disabled={submitting} disabled={submitting} onClick={onSubmit}>
                  {submitting ? 'Submitting…' : 'Submit responses'}
                </SubmitButton>
              </SubmitSectionInner>
            </SubmitSection>
            <PoweredBy>
              Powered by{' '}
              <a href="https://bandos.ai" target="_blank" rel="noopener noreferrer">
                Bandos
              </a>
            </PoweredBy>
          </Content>
        </Page>
      </AppContent>
    </AppLayout>
  )
}
